/**
 * Universal ChartSection Component
 * Event-driven wrapper for all chart information sections
 * Provides lazy loading, CSS visibility control, and performance optimization
 */

import { devWarn } from '../../../utils/devLog';
import React, { useState, useEffect, useRef } from 'react';
import { globalEventStream } from './universal_architecture/core/EventStream';
import { ChartNavigationEventType, ChartNavigationEvent } from './universal_architecture/types/EventTypes';

interface ChartSectionProps {
  /** The React component to render */
  component: React.ComponentType<any>;
  /** Unique identifier for this section */
  sectionId: string;
  /** Chart ID this section belongs to */
  chartId?: string;
  /** Props to pass to the wrapped component */
  componentProps?: Record<string, any>;
  /** Event types this section should listen to */
  dependencies?: string[];
  /** CSS class name for the section container */
  className?: string;
  /** Whether to render immediately (for active chart) */
  initiallyVisible?: boolean;
  /** Performance monitoring callback */
  onRenderTime?: (sectionId: string, renderTime: number) => void;
}

/**
 * Universal wrapper component for chart information sections
 * Handles activation/deactivation through events instead of React re-renders
 */
export const ChartSection: React.FC<ChartSectionProps> = ({
  component: Component,
  sectionId,
  chartId,
  componentProps = {},
  dependencies = ['chart:activated', 'chart:deactivated'],
  className = '',
  initiallyVisible = false,
  onRenderTime
}) => {
  // Visibility state (managed through events, not props)
  const [isVisible, setIsVisible] = useState(initiallyVisible);
  const [isDataLoaded, setIsDataLoaded] = useState(initiallyVisible);

  // Performance tracking - start time will be set when section becomes visible
  // For initiallyVisible sections, start measuring immediately
  const renderStartTime = useRef<number>(initiallyVisible ? performance.now() : 0);
  const hasRendered = useRef<boolean>(false);

  // Simple props pass-through - EditableField handles its own state via events
  const memoizedProps = componentProps;

  // Event listeners for chart activation/deactivation
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Listen to chart activation events
    if (dependencies.includes('chart:activated')) {
      const unsubscribeActivated = globalEventStream.subscribe(
        ChartNavigationEventType.CHART_ACTIVATED,
        (event: ChartNavigationEvent) => {
          if (event.chartId === chartId) {
            // Start performance measurement when section becomes visible
            if (!hasRendered.current) {
              renderStartTime.current = performance.now();
            }

            setIsVisible(true);
            setIsDataLoaded(true);
          }
        }
      );
      unsubscribers.push(() => unsubscribeActivated.unsubscribe());
    }

    // Listen to chart deactivation events
    if (dependencies.includes('chart:deactivated')) {
      const unsubscribeDeactivated = globalEventStream.subscribe(
        ChartNavigationEventType.CHART_DEACTIVATED,
        (event: ChartNavigationEvent) => {
          if (event.chartId === chartId) {
            setIsVisible(false);
            // Keep data loaded for instant re-activation
          }
        }
      );
      unsubscribers.push(() => unsubscribeDeactivated.unsubscribe());
    }

    // Cleanup all subscriptions
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [chartId, sectionId, dependencies]);

  // Performance monitoring
  useEffect(() => {
    if (isVisible && !hasRendered.current) {
      hasRendered.current = true;

      // Measure render time on next tick
      setTimeout(() => {
        const renderTime = performance.now() - renderStartTime.current;

        if (onRenderTime) {
          onRenderTime(sectionId, renderTime);
        }
      }, 0);
    }
  }, [isVisible, sectionId, onRenderTime]);

  // CSS classes for visibility control
  const containerClasses = [
    'chart-section',
    `chart-section-${sectionId}`,
    isVisible ? 'chart-section-visible' : 'chart-section-hidden',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} data-section-id={sectionId} data-chart-id={chartId}>
      {/* Render once when data is loaded, then toggle visibility via CSS */}
      {isDataLoaded && (
        <div style={{ display: isVisible ? 'block' : 'none' }}>
          <Component {...memoizedProps} />
        </div>
      )}
    </div>
  );
};

/**
 * HOC for converting existing components to use ChartSection wrapper
 */
export function withChartSection<T extends Record<string, any>>(
  Component: React.ComponentType<T>,
  sectionId: string,
  options: {
    dependencies?: string[];
    className?: string;
  } = {}
) {
  return React.memo((props: T & { chartId?: string }) => {
    const { chartId, ...componentProps } = props;

    return (
      <ChartSection
        component={Component}
        sectionId={sectionId}
        chartId={chartId}
        componentProps={componentProps}
        dependencies={options.dependencies}
        className={options.className}
      />
    );
  });
}

/**
 * Hook for section performance monitoring
 */
export function useSectionPerformance() {
  const renderTimes = useRef<Map<string, number>>(new Map());

  const recordRenderTime = (sectionId: string, renderTime: number) => {
    renderTimes.current.set(sectionId, renderTime);

    // Warn about slow sections (150ms threshold for initial render with data processing)
    if (renderTime > 150) {
      devWarn(`🐌 [Performance] Slow section render: ${sectionId} (${renderTime.toFixed(2)}ms)`);
    }
  };

  const getAverageRenderTime = () => {
    const times = Array.from(renderTimes.current.values());
    return times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
  };

  const getSectionStats = () => {
    return {
      totalSections: renderTimes.current.size,
      averageRenderTime: getAverageRenderTime(),
      slowestSection: Math.max(...Array.from(renderTimes.current.values()), 0),
      renderTimes: Object.fromEntries(renderTimes.current)
    };
  };

  return {
    recordRenderTime,
    getSectionStats
  };
}